// Skill tree panel — grid view of class skills with rank/max, invest button.
// Simpler than a true tree renderer: linear grid works for the current skill
// system (no branching prerequisites wired yet). Opens on 'K'.

import type { Net } from "../net.js";
import type { ClientStore, ClientState } from "../state.js";

export function openSkillTree(host: HTMLElement, net: Net, store: ClientStore): HTMLElement {
  const existing = host.querySelector<HTMLElement>(".skill-overlay");
  if (existing) { existing.remove(); return existing; }

  const overlay = document.createElement("div");
  overlay.className = "skill-overlay";
  host.appendChild(overlay);

  net.send({ t: "skill_tree" });

  function render(s: ClientState): void {
    const tree = s.skill_tree;
    overlay.innerHTML = `
      <div class="skill-modal" role="dialog" aria-label="Skill Tree">
        <header class="skill-header">
          <h2>Skill Tree ${tree ? `— ${escape(tree.class_name)}` : ""}</h2>
          ${tree ? `<span class="skill-points">${tree.points_available} point${tree.points_available === 1 ? "" : "s"} available</span>` : ""}
          <button id="skill-close" class="skill-close" aria-label="Close">×</button>
        </header>
        <section class="skill-grid">
          ${tree ? tree.skills.map((sk) => renderNode(sk, tree.points_available > 0)).join("") : `<p class="dim">Loading…</p>`}
        </section>
      </div>
    `;
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("[data-train]")) {
      b.addEventListener("click", () => net.send({ t: "train_skill", skill_id: b.dataset.train! }));
    }
    overlay.querySelector<HTMLButtonElement>("#skill-close")?.addEventListener("click", () => cleanup());
  }

  function renderNode(
    sk: { id: string; name: string; description: string; rank: number; max_rank: number },
    canTrain: boolean,
  ): string {
    const maxed = sk.rank >= sk.max_rank;
    const untrained = sk.rank === 0;
    const pct = Math.round((sk.rank / Math.max(1, sk.max_rank)) * 100);
    return `
      <article class="skill-node ${maxed ? "maxed" : ""} ${untrained ? "untrained" : ""}">
        <h3>${escape(sk.name)}</h3>
        <div class="skill-rank">rank ${sk.rank} / ${sk.max_rank}</div>
        <div class="skill-track"><div class="skill-fill" style="width:${pct}%"></div></div>
        <p class="skill-desc dim">${escape(sk.description)}</p>
        ${maxed
          ? `<button class="skill-action" disabled>Maxed</button>`
          : canTrain
            ? `<button class="skill-action primary" data-train="${escape(sk.id)}">Train (+1)</button>`
            : `<button class="skill-action" disabled>Need points</button>`}
      </article>
    `;
  }

  let cleanup: () => void = () => { /* set below */ };
  const unsubscribe = store.subscribe(render);
  render(store.get());
  cleanup = (): void => {
    unsubscribe();
    document.removeEventListener("keydown", onEsc);
    overlay.remove();
    store.closePanel();
  };
  function onEsc(ev: KeyboardEvent): void { if (ev.key === "Escape") cleanup(); }
  (overlay as unknown as { __cleanup: () => void }).__cleanup = cleanup;
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(); });
  document.addEventListener("keydown", onEsc);
  return overlay;
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
